import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REST_SECONDS, WORKOUT_SECONDS, workouts } from './workouts';

type Phase = 'exercise' | 'rest' | 'complete';
type SoundName = 'whistle' | 'whistle2' | 'harp';
type BrowserWindow = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

type SessionState = {
  exerciseIndex: number;
  phase: Phase;
  phaseElapsed: number;
  totalElapsed: number;
  isRunning: boolean;
};

const initialSession: SessionState = {
  exerciseIndex: 0,
  phase: 'exercise',
  phaseElapsed: 0,
  totalElapsed: 0,
  isRunning: false,
};

const audioSprite: Record<SoundName, { start: number; end: number }> = {
  whistle: { start: 0, end: 1.3 },
  whistle2: { start: 3, end: 4.1 },
  harp: { start: 6, end: 8 },
};

function assetUrl(path: string) {
  return `${import.meta.env.BASE_URL}${path.replace(/^\//, '')}`;
}

const AUDIO_SPRITE_URL = assetUrl('audio/soundefx.mp3');

function formatTime(totalSeconds: number) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds - minutes * 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export default function App() {
  const [session, setSession] = useState<SessionState>(initialSession);
  const [showTotalTime, setShowTotalTime] = useState(false);
  const [isInfoOpen, setInfoOpen] = useState(false);
  const [isSwitchNoticeOpen, setSwitchNoticeOpen] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const audioBufferPromiseRef = useRef<Promise<AudioBuffer | null> | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);
  const switchNoticeKeyRef = useRef<string | null>(null);
  const previousSessionRef = useRef<Pick<SessionState, 'exerciseIndex' | 'phase'>>({
    exerciseIndex: initialSession.exerciseIndex,
    phase: initialSession.phase,
  });

  const currentWorkout = workouts[session.exerciseIndex];
  const countdownSeconds =
    session.phase === 'rest'
      ? REST_SECONDS - session.phaseElapsed
      : Math.max(WORKOUT_SECONDS - session.phaseElapsed, 0);
  const displayedTime = showTotalTime ? session.totalElapsed : countdownSeconds;
  const isTimerWarning = !showTotalTime && session.phase === 'exercise' && countdownSeconds < 5;
  const canGoPrevious = session.exerciseIndex > 0;
  const canGoNext = session.exerciseIndex < workouts.length - 1;

  const imageSrc = useMemo(() => assetUrl(`img/${currentWorkout.id}.png`), [currentWorkout.id]);
  const imageSrcSet = useMemo(
    () =>
      `${assetUrl(`img/${currentWorkout.id}.png`)} 1x, ${assetUrl(
        `img/${currentWorkout.id}@2x.png`,
      )} 2x`,
    [currentWorkout.id],
  );

  const getAudioContext = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;

    const AudioContextConstructor =
      window.AudioContext ?? (window as BrowserWindow).webkitAudioContext;
    if (!AudioContextConstructor) return null;

    audioContextRef.current = new AudioContextConstructor();
    return audioContextRef.current;
  }, []);

  const loadAudioBuffer = useCallback(() => {
    if (audioBufferRef.current) {
      return Promise.resolve(audioBufferRef.current);
    }

    if (audioBufferPromiseRef.current) {
      return audioBufferPromiseRef.current;
    }

    const audioContext = getAudioContext();
    if (!audioContext) {
      return Promise.resolve(null);
    }

    audioBufferPromiseRef.current = fetch(AUDIO_SPRITE_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to load audio sprite: ${response.status}`);
        }
        return response.arrayBuffer();
      })
      .then((arrayBuffer) => audioContext.decodeAudioData(arrayBuffer))
      .then((audioBuffer) => {
        audioBufferRef.current = audioBuffer;
        return audioBuffer;
      })
      .catch(() => {
        audioBufferPromiseRef.current = null;
        return null;
      });

    return audioBufferPromiseRef.current;
  }, [getAudioContext]);

  const unlockAudio = useCallback(() => {
    const audioContext = getAudioContext();
    if (!audioContext) return;

    if (audioContext.state === 'suspended') {
      void audioContext.resume();
    }
    void loadAudioBuffer();
  }, [getAudioContext, loadAudioBuffer]);

  const playSound = useCallback(
    (sound: SoundName) => {
      const audioContext = getAudioContext();
      if (!audioContext) return;

      const playBuffer = (audioBuffer: AudioBuffer) => {
        if (audioContext.state === 'suspended') {
          void audioContext.resume();
        }

        const sprite = audioSprite[sound];
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);
        source.start(0, sprite.start, sprite.end - sprite.start);
      };

      if (audioBufferRef.current) {
        playBuffer(audioBufferRef.current);
        return;
      }

      void loadAudioBuffer().then((audioBuffer) => {
        if (audioBuffer) {
          playBuffer(audioBuffer);
        }
      });
    },
    [getAudioContext, loadAudioBuffer],
  );

  const resetWorkout = useCallback(() => {
    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
    previousSessionRef.current = {
      exerciseIndex: initialSession.exerciseIndex,
      phase: initialSession.phase,
    };
    setSession(initialSession);
    setSwitchNoticeOpen(false);
  }, []);

  const startPauseWorkout = useCallback(() => {
    unlockAudio();

    if (session.phase === 'complete') {
      previousSessionRef.current = {
        exerciseIndex: initialSession.exerciseIndex,
        phase: initialSession.phase,
      };
      playSound('whistle');
      setSession({ ...initialSession, isRunning: true });
      return;
    }

    if (!session.isRunning) {
      playSound('whistle');
    }

    setSession((current) => ({ ...current, isRunning: !current.isRunning }));
  }, [playSound, session.isRunning, session.phase, unlockAudio]);

  const jumpToExercise = useCallback(
    (direction: -1 | 1) => {
      const nextExerciseIndex = session.exerciseIndex + direction;
      if (nextExerciseIndex < 0 || nextExerciseIndex >= workouts.length) return;

      unlockAudio();
      if (resetTimeoutRef.current !== null) {
        window.clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = null;
      }

      const nextSession: SessionState = {
        exerciseIndex: nextExerciseIndex,
        phase: 'exercise',
        phaseElapsed: 0,
        totalElapsed: 0,
        isRunning: true,
      };

      previousSessionRef.current = {
        exerciseIndex: nextSession.exerciseIndex,
        phase: nextSession.phase,
      };
      setSwitchNoticeOpen(false);
      setShowTotalTime(false);
      setSession(nextSession);
      playSound('whistle');
    },
    [playSound, session.exerciseIndex, unlockAudio],
  );

  useEffect(() => {
    return () => {
      void audioContextRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (!session.isRunning || session.phase === 'complete') return undefined;

    const interval = window.setInterval(() => {
      setSession((current) => {
        if (!current.isRunning || current.phase === 'complete') return current;

        const totalElapsed = current.totalElapsed + 1;

        if (current.phase === 'exercise') {
          const nextPhaseElapsed = current.phaseElapsed + 1;
          if (nextPhaseElapsed >= WORKOUT_SECONDS) {
            if (current.exerciseIndex === workouts.length - 1) {
              return {
                ...current,
                phase: 'complete',
                phaseElapsed: WORKOUT_SECONDS,
                totalElapsed,
                isRunning: false,
              };
            }

            return {
              ...current,
              exerciseIndex: current.exerciseIndex + 1,
              phase: 'rest',
              phaseElapsed: 0,
              totalElapsed,
            };
          }

          return { ...current, phaseElapsed: nextPhaseElapsed, totalElapsed };
        }

        const nextPhaseElapsed = current.phaseElapsed + 1;
        if (nextPhaseElapsed >= REST_SECONDS) {
          return {
            ...current,
            phase: 'exercise',
            phaseElapsed: 0,
            totalElapsed,
          };
        }

        return { ...current, phaseElapsed: nextPhaseElapsed, totalElapsed };
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [session.isRunning, session.phase]);

  useEffect(() => {
    const previousSession = previousSessionRef.current;
    const didPhaseChange = previousSession.phase !== session.phase;
    const didExerciseChange = previousSession.exerciseIndex !== session.exerciseIndex;

    if (didExerciseChange && session.phase === 'rest') {
      playSound('whistle2');
    } else if (didPhaseChange && session.phase === 'exercise' && previousSession.phase === 'rest') {
      playSound('whistle');
    } else if (didPhaseChange && session.phase === 'complete') {
      playSound('harp');
      resetTimeoutRef.current = window.setTimeout(resetWorkout, 5000);
    }

    previousSessionRef.current = {
      exerciseIndex: session.exerciseIndex,
      phase: session.phase,
    };
  }, [playSound, resetWorkout, session.exerciseIndex, session.phase]);

  useEffect(() => {
    if (
      session.phase === 'exercise' &&
      currentWorkout.id === 12 &&
      session.phaseElapsed === 15 &&
      session.isRunning
    ) {
      const key = `${currentWorkout.id}-${session.totalElapsed}`;
      if (switchNoticeKeyRef.current === key) return;
      switchNoticeKeyRef.current = key;
      setSwitchNoticeOpen(true);
      playSound('whistle');
      window.setTimeout(() => setSwitchNoticeOpen(false), 3000);
    }
  }, [currentWorkout.id, playSound, session.isRunning, session.phase, session.phaseElapsed, session.totalElapsed]);

  const title = session.phase === 'complete' ? "You're done! Great work!" : currentWorkout.name;

  return (
    <main className="app-shell" aria-label="7 Minute Workout">
      <section className="workout-stage" aria-live="polite">
        <header className="workout-header">
          <h1>{title}</h1>
          <button className="info-button" type="button" onClick={() => setInfoOpen(true)} aria-label="Workout info">
            i
          </button>
        </header>

        <span className="exercise-number" aria-hidden="true">
          {currentWorkout.id}
        </span>

        <img
          key={currentWorkout.id}
          className="workout-image"
          src={imageSrc}
          srcSet={imageSrcSet}
          alt={currentWorkout.name}
          draggable="false"
        />
      </section>

      <footer className="control-bar">
        <button
          className="sprite-button icon-button is-previous"
          type="button"
          onClick={() => jumpToExercise(-1)}
          disabled={!canGoPrevious}
          aria-label="Start previous exercise"
        >
          <span aria-hidden="true" />
        </button>
        <span className="divider" aria-hidden="true" />
        <button
          className={`sprite-button ${session.isRunning ? 'is-pause' : 'is-play'}`}
          type="button"
          onClick={startPauseWorkout}
          aria-label={session.isRunning ? 'Pause workout' : 'Start workout'}
        >
          <span aria-hidden="true" />
        </button>
        <span className="divider" aria-hidden="true" />
        <button className="sprite-button is-stop" type="button" onClick={resetWorkout} aria-label="Reset workout">
          <span aria-hidden="true" />
        </button>
        <span className="divider" aria-hidden="true" />
        <button
          className="sprite-button icon-button is-next"
          type="button"
          onClick={() => jumpToExercise(1)}
          disabled={!canGoNext}
          aria-label="Start next exercise"
        >
          <span aria-hidden="true" />
        </button>
        <span className="divider" aria-hidden="true" />
        <button className={`timer ${isTimerWarning ? 'timer-warning' : ''}`} type="button" onClick={() => setShowTotalTime((value) => !value)}>
          {formatTime(displayedTime)}
        </button>
      </footer>

      {session.phase === 'rest' && (
        <NoticeModal title={`BREAK! Up next... ${currentWorkout.name}`}>
          Tap the screen to keep your phone active.
        </NoticeModal>
      )}

      {isSwitchNoticeOpen && (
        <NoticeModal title="SWITCH SIDES!">
          Keep your form tight.
        </NoticeModal>
      )}

      {isInfoOpen && <InfoModal onClose={() => setInfoOpen(false)} />}
    </main>
  );
}

function NoticeModal({ title, children }: { title: string; children: string }) {
  return (
    <div className="modal-screen" role="presentation">
      <div className="notice-modal" role="status" aria-live="assertive">
        <h2>{title}</h2>
        <p>{children}</p>
      </div>
    </div>
  );
}

function InfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-screen modal-screen-dark" role="presentation" onClick={onClose}>
      <article className="info-modal" role="dialog" aria-modal="true" aria-labelledby="info-title" onClick={(event) => event.stopPropagation()}>
        <header>
          <h2 id="info-title">7 Minute Workout</h2>
          <button type="button" onClick={onClose} aria-label="Close workout info">
            x
          </button>
        </header>
        <div className="info-content">
          <h3>About</h3>
          <p>
            The exercises should be performed in rapid succession, allowing 30 seconds for each with a 10-second rest between exercises. Keep the intensity high, then you are done.
          </p>
          <h3>Credits</h3>
          <p>
            Programming:{' '}
            <a href="http://willwilson.co/" target="_blank" rel="noreferrer">
              Will Wilson
            </a>
          </p>
          <p>
            Science:{' '}
            <a
              href="http://journals.lww.com/acsm-healthfitness/Fulltext/2013/05000/HIGH_INTENSITY_CIRCUIT_TRAINING_USING_BODY_WEIGHT_.5.aspx"
              target="_blank"
              rel="noreferrer"
            >
              ACSM&apos;s Health &amp; Fitness Journal
            </a>
          </p>
          <p>
            Excerpt From:{' '}
            <a href="http://lifehacker.com/this-7-minute-research-based-workout-exercises-your-wh-498676327" target="_blank" rel="noreferrer">
              lifehacker.com
            </a>
          </p>
          <button className="ok-button" type="button" onClick={onClose}>
            Ok
          </button>
        </div>
      </article>
    </div>
  );
}
